import { saveAs } from 'file-saver'
import $http from '@/libs/axios'
import FormsService from '@/services/forms-service'

export type OrderTransferPayload = {
  order_ids: number[];
  target_branch_id: number;
  reason?: string;
}

export type TransferBranch = {
  id: number;
  name: string;
}

export type TransferHistoryEntry = {
  id: number;
  order_id: number;
  old_branch_id: number;
  old_branch_name: string;
  new_branch_id: number;
  new_branch_name: string;
  user_id: number;
  subuser_id: number | null;
  transferred_by?: string;
  state?: string;
  status?: string;
  reason: string | null;
  created_at: string;
}

export type TransferHistoryStats = {
  total: number;
  successful: number;
  failed: number;
  today: number;
  this_week: number;
}

export type TransferHistoryResponse = {
  data: TransferHistoryEntry[];
  stats?: TransferHistoryStats;
  meta?: Record<string, unknown>;
  links?: Record<string, unknown>;
}

export type CancelDeliveryProviderPayload = {
  id: number;
  branch_id: number;
}

class OrdersAPI {
  static async updateOrder(payload: any) {
    return (await $http.put('/orders/update', payload)).data
  }

  static async refundOrder(orderId: any) {
    return (await $http.post(`/orders/refund/${orderId}`)).data
  }

  static async returnOrder(payload: any) {
    return (await $http.post('pos/returned-orders', payload)).data
  }

  static async getSettings(branchId: number) {
    return (await $http.get(`/orders/settings/${branchId}`)).data
  }

  static async getOrder(id: number) {
    return (await $http.get(`/orders/order/${id}`)).data
  }

  static async getOrderPublicInvoice(businessId: number, id: number) {
    const { data } = await $http.get(`/business/${businessId}/order/${id}`)
    return {
      order: data.order,
      paymentMethods: data.paymentMethods ?? [],
      businessLogo: data.businessLogo,
    }
  }

  static async editOrder(orderData: any) {
    return (await $http.post('/orders/edit-order', orderData)).data
  }

  static async getOrderHistories(id: number) {
    return (await $http.get(`/orders/order/${id}/history`)).data
  }

  static async getOrderInvoice(id: number) {
    return (await $http.get(`/orders/order/${id}/invoice`)).data
  }

  static async downloadOrder(id: number, download = true) {
    const url = `/orders/order/${id}/invoice?download=1`
    const file = (await $http.get(url, { responseType: 'blob' })).data
    if (download) {
      saveAs(file, `Order-${id}.pdf`)
    }
    return file
  }

  static async printOrder(id: number) {
    const file = await OrdersAPI.downloadOrder(id, false)
    return URL.createObjectURL(file)
  }

  static async reloadDeliveryDetails(deliveryCode: number) {
    return (await $http.get(`/service/delivery/fetch-order/${deliveryCode}`)).data.delivery
  }

  static async cancelDeliveryProvider(payload: CancelDeliveryProviderPayload) {
    return (await $http.put('/orders/cancel/delivery', payload)).data
  }

  static async exportExcel(filters: any) {
    const url = `/orders/export?${FormsService.prepareQueryString(filters, true)}`
    const file = (await $http.get(url, { responseType: 'blob' })).data
    saveAs(file, 'orders.xlsx')
  }

  static async verifyPayment(id: number) {
    return (await $http.get(`/orders/verify-payment?orderId=${id}`)).data
  }

  public static async addNotes(id: number, body: any) {
    return (await $http.post(`orders/notes/${id}`, body)).data
  }

  public static async unverifiedPaymentsCount() {
    return (await $http.get('orders/unverified-orders/count?only_unverified=1')).data
  }

  public static async getTables(branchId: any) {
    return (await $http.get(`webapp/tables/${branchId}`, { headers: { webapp: 'true' } })).data
  }

  public static async cars() {
    const { models, colors } = (await $http.get('webapp/cars-colors')).data
    const toUniqueOptions = (record: Record<string, string>) => {
      const seen = new Set<string>()
      return Object.keys(record).reduce<Array<{ value: string; label: string }>>((options, key) => {
        const value = record[key]
        if (!value || seen.has(value)) return options
        seen.add(value)
        options.push({ value, label: value })
        return options
      }, [])
    }
    return {
      colors: toUniqueOptions(colors),
      models: toUniqueOptions(models),
    }
  }

  public static async addressHistory(phoneNumber: any) {
    return (await $http.get(`pos/order/address-history/${phoneNumber}`)).data.addresses
  }

  public static async searchCustomers(phone: any) {
    return (await $http.get(`/clients/list?phone=${phone}`)).data
  }

  public static async getZones() {
    return (await $http.get('/all-zones')).data
  }

  public static async getShifts(referenceId: any) {
    return (await $http.get(`webapp/v5/shifts/${referenceId}`)).data
  }

  public static async createOrder(orderData: any) {
    return (await $http.post('/orders/create-order', orderData)).data
  }

  static async getTransferBranches(excludeBranchId?: number) {
    const params = excludeBranchId ? { exclude_branch_id: excludeBranchId } : {}
    const { data } = await $http.get('/orders/transfer/branches', { params })
    return (data?.branches ?? []) as TransferBranch[]
  }

  static async transferOrder(payload: OrderTransferPayload) {
    return (await $http.post('/orders/transfer', payload)).data
  }

  static async getTransferHistory(params?: Record<string, unknown>) {
    return (await $http.get('/orders/transfer/history', { params })).data as TransferHistoryResponse
  }
}

export default OrdersAPI
